import { transformSrcsetCandidates } from '@/app/_lib/image-art-direction';
import type { TransformSrcsetCandidatesResult } from '@/app/_lib/image-art-direction';
import {
    MEDIA_DESKTOP,
    SIZES_BASE,
    SIZES_DESKTOP,
    SIZES_DESKTOP_SQUARE,
    SLIDE_BASIS_LANDSCAPE,
    SLIDE_BASIS_PORTRAIT,
    SLIDE_BASIS_SQUARE,
    type SlidesPerViewKey,
} from './carousel.constants';
import type { CarouselImage } from './carousel.types';

/**
 * Gets the CSS basis class for a carousel item based on configuration
 */
export function getItemBasisClass(
    slidesPerView: number,
    isSquare: boolean,
    isPortrait: boolean
): string {
    const basisKey: SlidesPerViewKey =
        (slidesPerView as SlidesPerViewKey) in SLIDE_BASIS_LANDSCAPE
            ? (slidesPerView as SlidesPerViewKey)
            : 4;

    if (isSquare) {
        return SLIDE_BASIS_SQUARE[basisKey];
    }

    return isPortrait
        ? SLIDE_BASIS_PORTRAIT[basisKey]
        : SLIDE_BASIS_LANDSCAPE[basisKey];
}

/**
 * Determines if an index should use portrait orientation on desktop
 * Pattern: [L, P, P, L, L, P, P, L, ...] - index 1,2,5,6,9,10... are portrait
 * TODO: This pattern should come from T3/backend per customer
 */
export function isPortraitIndex(index: number): boolean {
    const positionInGroup = index % 4;
    return positionInGroup === 1 || positionInGroup === 2;
}

/**
 * Builds the responsive sizes attribute for an image based on display configuration
 *
 * When used with art direction, we split sizes:
 * - Desktop source gets only desktop sizes (landscape)
 * - Fallback source gets only mobile/tablet sizes
 *
 * @param breakpointScope - 'all' for combined, 'desktop' for lg+ only, 'mobile' for below lg
 */
export function buildImageSizes(
    slidesPerView: number,
    displayType: string,
    isPortrait: boolean,
    breakpointScope: 'all' | 'desktop' | 'mobile' = 'all'
): string {
    const key = (slidesPerView as SlidesPerViewKey) in SIZES_DESKTOP
        ? (slidesPerView as SlidesPerViewKey)
        : 4;

    const isSquare = displayType === 'square';

    // Mobile/tablet sizes (below lg breakpoint)
    const mobileSizes = SIZES_BASE[key];

    // Desktop sizes (lg+ breakpoint)
    let desktopSizes: string;
    if (isSquare) {
        desktopSizes = SIZES_DESKTOP_SQUARE[key];
    } else {
        const sizesByOrientation = SIZES_DESKTOP[key];
        desktopSizes = isPortrait ? sizesByOrientation.portrait : sizesByOrientation.landscape;
    }

    // Return based on scope
    if (breakpointScope === 'desktop') {
        return desktopSizes;
    }
    if (breakpointScope === 'mobile') {
        return mobileSizes;
    }
    return `${desktopSizes}, ${mobileSizes}`;
}

/**
 * Transforms srcset candidates with art direction based on layout configuration
 *
 * Art direction is only needed when the image changes between breakpoints:
 * - Portrait indices (1,2,5,6...): Always portrait -> no art direction needed
 * - Landscape indices (0,3,4,7...): Portrait on mobile, landscape on desktop -> art direction needed
 *
 * When art direction is used, each source gets its own sizes attribute:
 * - Desktop source: only desktop sizes (landscape)
 * - Fallback source: only mobile/tablet sizes
 */
export function buildArtDirectedSrcset(
    image: CarouselImage,
    layoutSplit: boolean,
    isPortrait: boolean,
    slidesPerView = 4,
    displayType = 'lp'
): TransformSrcsetCandidatesResult {
    const candidates = image.srcsetCandidates;

    // Array format - no art direction
    if (Array.isArray(candidates)) {
        return transformSrcsetCandidates(candidates);
    }

    // Square format - no art direction, just use square as fallback
    if (candidates.square) {
        return transformSrcsetCandidates(candidates.square);
    }

    // Portrait images (layoutSplit enabled, portrait index):
    // Always portrait on all viewports -> no art direction needed
    if (layoutSplit && isPortrait) {
        return transformSrcsetCandidates(candidates.portrait ?? candidates.landscape ?? []);
    }

    // If layoutSplit is disabled, use default behavior (portrait mobile, landscape desktop)
    if (!layoutSplit) {
        // Desktop sizes for landscape orientation
        const desktopSizes = buildImageSizes(slidesPerView, displayType, false, 'desktop');

        return transformSrcsetCandidates({
            fallback: candidates.portrait ?? candidates.landscape ?? [],
            variants: candidates.landscape
                ? [{ media: MEDIA_DESKTOP, srcsetCandidates: candidates.landscape, sizes: desktopSizes }]
                : [],
        });
    }

    // Landscape images (layoutSplit enabled, landscape index):
    // Portrait on mobile, landscape on desktop -> art direction needed
    // Desktop sizes for landscape orientation (isPortrait=false because this is a landscape-index image)
    const desktopSizes = buildImageSizes(slidesPerView, displayType, false, 'desktop');

    return transformSrcsetCandidates({
        fallback: candidates.portrait ?? candidates.landscape ?? [],
        variants: candidates.landscape
            ? [{ media: MEDIA_DESKTOP, srcsetCandidates: candidates.landscape, sizes: desktopSizes }]
            : [],
    });
}

/**
 * Gets the aspect ratio class based on display type and orientation
 */
export function getAspectRatioClass(displayType: string, isPortrait: boolean): string {
    if (displayType === 'square') {
        return 'aspect-square';
    }

    if (isPortrait) {
        return 'aspect-3/4';
    }

    // Landscape: 3:4 on mobile, 4:3 on desktop
    return 'aspect-3/4 lg:aspect-4/3';
}
